
import React, { useState, useRef, useEffect } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import { AudioVisualizer } from "./AudioVisualizer";
import { X, Square, Pause, Play, AlertTriangle } from "lucide-react";
import { RecordingTimer } from "./RecordingTimer";
import { useToast } from "../../ui/use-toast";

interface RecordingInterfaceProps {
  onCancel: () => void;
  onComplete: (blob: Blob, wasCancelled: boolean) => void;
  title?: string;
}

export function RecordingInterface({ onCancel, onComplete, title = "Recording" }: RecordingInterfaceProps) {
  const [duration, setDuration] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [wasCancelled, setWasCancelled] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const { toast } = useToast();

  const {
    status,
    startRecording,
    stopRecording,
    resumeRecording,
    pauseRecording,
    clearBlobUrl,
  } = useReactMediaRecorder({
    audio: true,
    blobPropertyBag: {
      type: "audio/webm;codecs=opus"
    },
    onStop: async (_, blob) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        mediaStreamRef.current = null;
      }
      setIsSaving(true);
      await onComplete(blob, wasCancelled);
      setIsSaving(false);
      clearBlobUrl();
    },
    mediaRecorderOptions: {
      audioBitsPerSecond: 128000, // High quality audio
      mimeType: 'audio/webm;codecs=opus'
    },
    onError: (err) => {
      console.error("Media recorder error:", err);
      setPermissionError("Error accessing microphone. Please check permissions.");
      toast({
        title: "Recording Error",
        description: "Could not access microphone. Please check your browser permissions.",
        variant: "destructive",
      });
    }
  });

  const handleCancel = () => {
    setWasCancelled(true);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }
    stopRecording();
    setDuration(0);
    onCancel();
  };

  useEffect(() => {
    const startRecordingImmediately = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            channelCount: 2,
            sampleRate: 48000,
            sampleSize: 16,
            echoCancellation: true,
            noiseSuppression: true
          }
        });
        mediaStreamRef.current = stream;
        setDuration(0);
        setIsPaused(false);
        startRecording();
        timerRef.current = setInterval(() => {
          setDuration(prev => prev + 1);
        }, 1000);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        setPermissionError(error instanceof Error ? error.message : "Could not access microphone");
        toast({
          title: "Microphone Access Denied",
          description: "Please allow microphone access in your browser settings and try again.",
          variant: "destructive",
        });
        // Don't immediately cancel - give the user a chance to see the error
        setTimeout(handleCancel, 3000);
      }
    };

    startRecordingImmediately();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        mediaStreamRef.current = null;
      }
    };
  }, []);

  const handlePauseResume = () => {
    if (isPaused) {
      resumeRecording();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } else {
      pauseRecording();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const handleStop = () => {
    setWasCancelled(false);
    setIsSaving(true);
    stopRecording();
  };

  if (permissionError) {
    return (
      <div className="flex items-center gap-4 px-6 py-3 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-2 border-red-300 rounded-full shadow-lg">
        <AlertTriangle className="h-5 w-5" />
        <span className="text-sm font-medium">{permissionError}</span>
        <button
          onClick={handleCancel}
          className="ml-auto p-2 hover:bg-red-100 dark:hover:bg-red-800/30 rounded-full transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 px-6 py-3 bg-[hsl(var(--recording-bg))] text-[hsl(var(--recording-text))] dark:bg-opacity-[var(--recording-opacity)] border-2 border-[hsl(var(--recording-border))] rounded-full shadow-lg">
      {isSaving ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          <span className="text-sm font-medium">Saving recording...</span>
        </>
      ) : (
        <>
          {mediaStreamRef.current && !isPaused && (
            <div className="w-24">
              <AudioVisualizer stream={mediaStreamRef.current} />
            </div>
          )}
          {isPaused && (
            <div className="w-24 flex items-center justify-center">
              <span className="text-sm font-medium">Paused</span>
            </div>
          )}
          
          <div className="flex items-center gap-4">
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Cancel recording"
            >
              <X className="h-4 w-4" />
            </button>

            <button
              onClick={handleStop}
              className="p-3 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
              aria-label="Stop recording"
            >
              <Square className="h-4 w-4 text-white" />
            </button>

            <button
              onClick={handlePauseResume}
              className="p-2 bg-yellow-400 rounded-full hover:bg-yellow-500 transition-colors"
              aria-label={isPaused ? "Resume recording" : "Pause recording"}
            >
              {isPaused ? (
                <Play className="h-4 w-4 text-white" />
              ) : (
                <Pause className="h-4 w-4 text-white" />
              )}
            </button>

            <RecordingTimer duration={duration} />
          </div>
        </>
      )}
    </div>
  );
}
